// ─────────────────────────────────────────────────────────────────────────────
// Autenticação e registro de acesso.
//
// O login não é do Supabase: é um webhook do n8n que valida contra o cadastro
// da Space. A resposta vem em formatos diferentes dependendo do fluxo do n8n,
// e a função abaixo aceita todos eles (era assim na versão 1.x e continua
// sendo — mudar isso é mexer no n8n, não aqui).
// ─────────────────────────────────────────────────────────────────────────────

import { WEBHOOKS } from '../config/ambiente';
import { tabelaOperador } from './supabase';
import { credenciaisDaResposta, avaliarChave } from './credenciaisLogin';

const ACESSO_WEBHOOK_URL = 'https://n8n.space-cloud.tech/webhook/orion-acesso-log';

/**
 * Nome próprio com as iniciais maiúsculas, mantendo "de/da/do/das/dos/e"
 * minúsculos. "JONATHAN DA SILVA" vira "Jonathan da Silva".
 */
export function toTitleCaseNome(nome) {
  if (!nome || typeof nome !== 'string') return nome || '';
  const conectores = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
  return nome
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) => (i > 0 && conectores.has(w)) ? w : (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Deixa passar a chave recebida, mas grita se ela não devia estar no navegador.
 *
 * Não recusa: recusar deixaria o operador sem banco por uma decisão que não é
 * dele. Mas passar em silêncio seria repetir o erro que originou toda esta
 * mudança — uma service_role no cliente, que ignora RLS e abre o projeto
 * inteiro. Se alguém configurar a chave errada no n8n, o console diz qual é.
 */
function conferirCredenciais(credenciais) {
  if (!credenciais) return null;

  const veredito = avaliarChave(credenciais.chave);
  if (!veredito.segura) {
    console.error(
      `[Orion] O login devolveu uma chave ${veredito.motivo}, que NÃO deve ficar `
      + 'no navegador: ela ignora as regras de acesso do banco. Troque no fluxo '
      + 'do n8n pela chave publishable.'
    );
  }
  return credenciais;
}

/**
 * Tenta autenticar no webhook do n8n.
 * Devolve { ok: true, nome, email } ou { ok: false, mensagem }.
 * Nunca lança: erro de rede também volta como { ok:false }.
 */
export async function autenticar(email, senha) {
  const mail = (email || '').trim();
  const pass = (senha || '').trim();

  if (!mail || !pass) return { ok: false, mensagem: 'Preencha e-mail e senha.' };
  if (!WEBHOOKS.auth) {
    return { ok: false, mensagem: 'URL de autenticação não configurada. Verifique o .env ou as configurações.' };
  }

  try {
    const res = await fetch(WEBHOOKS.auth, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: mail, senha: pass }),
    });
    const data = await res.json();

    // Aceita vários formatos de resposta do n8n
    const ok = data.success === true || data.autenticado === true || data.authenticated === true
      || (res.ok && (data.nome || data.operador_nome || data.name));

    if (!ok) {
      return { ok: false, mensagem: data.message || data.mensagem || data.error || 'Credenciais inválidas.' };
    }

    return {
      ok: true,
      nome: toTitleCaseNome(data.nome || data.operador_nome || data.name || mail.split('@')[0]),
      email: data.email || data.operador_email || mail,
      // A chave do banco, quando o fluxo do n8n a devolve. Vem só no login
      // aceito — este ponto do código não é alcançado por um login recusado.
      supabase: conferirCredenciais(credenciaisDaResposta(data)),
    };
  } catch (err) {
    return { ok: false, mensagem: `Erro de conexão: ${err.message}` };
  }
}

// ── Mapa de operadores ───────────────────────────────────────────────────────
// e-mail (minúsculo) → código do operador.
//
// A fonte é orion.operador, NÃO o antigo assets/operadores.xml. O XML era um
// retrato congelado do cadastro e divergia do banco (havia gente num e não no
// outro), o que fazia o mesmo operador ter código em um fluxo e nenhum em outro.

let mapaOperadores = null;

/** Carrega o mapa do banco. Em caso de falha mantém o mapa anterior. */
export async function carregarMapaOperadores() {
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_codigo, ope_email')
      .eq('ope_enabled', true);
    if (error || !Array.isArray(data)) throw new Error(error?.message || 'resposta inválida');

    const mapa = {};
    data.forEach(o => {
      const email = String(o.ope_email || '').trim().toLowerCase();
      if (email && Number.isFinite(o.ope_codigo)) mapa[email] = o.ope_codigo;
    });
    mapaOperadores = mapa;
  } catch (err) {
    // Perder os códigos no meio da sessão quebraria o envio de tickets —
    // por isso mantém o que já tinha em vez de zerar.
    console.warn('[Orion] Falha ao carregar operadores de orion.operador:', err.message);
  }
  return mapaOperadores || {};
}

/** Consulta síncrona. Devolve {} enquanto o login não carregou o mapa. */
export function obterMapaOperadores() {
  return mapaOperadores || {};
}

/** Código do operador a partir do e-mail. null quando não cadastrado. */
export function codigoDoOperador(email) {
  return obterMapaOperadores()[(email || '').trim().toLowerCase()] || null;
}

// ── Registro de acesso ───────────────────────────────────────────────────────

function montarRegistroAcesso(evento, operador) {
  const agora = new Date();
  const pad = n => String(n).padStart(2, '0');
  return {
    operador_nome: operador?.nome || null,
    operador_email: operador?.email || null,
    operador_codigo: codigoDoOperador(operador?.email),
    evento,
    data: `${agora.getFullYear()}-${pad(agora.getMonth() + 1)}-${pad(agora.getDate())}`,
    hora: `${pad(agora.getHours())}:${pad(agora.getMinutes())}:${pad(agora.getSeconds())}`,
    registrado_em: agora.toISOString(),
  };
}

/** Registra login/logout no n8n. Dispara e esquece — nunca bloqueia a tela. */
export function registrarAcesso(evento, operador) {
  try {
    fetch(ACESSO_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(montarRegistroAcesso(evento, operador)),
    }).catch(err => console.warn('[Orion] Falha ao registrar acesso:', err.message));
  } catch (err) { console.warn('[Orion] Acesso:', err.message); }
}

/**
 * Registro de logout na saída da página.
 * sendBeacon é o único jeito confiável durante o unload: a requisição segue
 * mesmo com a página sendo descarregada. fetch comum é cancelado.
 */
export function registrarLogoutNaSaida(operador) {
  if (!operador?.email) return;
  try {
    const blob = new Blob([JSON.stringify(montarRegistroAcesso('logout', operador))], { type: 'application/json' });
    navigator.sendBeacon(ACESSO_WEBHOOK_URL, blob);
  } catch (e) { /* silencioso */ }
}
